import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Параметры для хука обратного отсчета
 */
interface UseCountdownOptions {
  /**
   * Начальное значение таймера в секундах
   * @default 0
   */
  initialSeconds?: number;

  /**
   * Автоматически запускать таймер при монтировании
   * @default false
   */
  autoStart?: boolean;

  /**
   * Колбэк, вызываемый при завершении обратного отсчета
   */
  onComplete?: () => void;

  /**
   * Интервал обновления в миллисекундах
   * @default 1000
   */
  interval?: number;
}

/**
 * Результат работы хука useCountdown
 */
interface UseCountdownResult {
  /**
   * Текущее значение счетчика (в секундах)
   */
  seconds: number;

  /**
   * Флаг активности таймера
   */
  isActive: boolean;

  /**
   * Запустить таймер с указанным значением
   * @param seconds - количество секунд для отсчета (если не указано, используется initialSeconds)
   */
  start: (seconds?: number) => void;

  /**
   * Сбросить и остановить таймер
   */
  reset: () => void;

  /**
   * Приостановить таймер
   */
  pause: () => void;

  /**
   * Возобновить таймер
   */
  resume: () => void;

  /**
   * Проверка, завершен ли отсчет
   */
  isCompleted: boolean;
}

/**
 * Хук для создания таймера обратного отсчета
 *
 * @param options Настройки таймера
 * @returns Объект для управления таймером
 *
 * @example
 * // Простое использование
 * const { seconds, isActive, start, reset } = useCountdown({ initialSeconds: 60 });
 *
 * @example
 * // Использование с автозапуском и обработкой завершения
 * const { seconds, isActive } = useCountdown({
 *   initialSeconds: 30,
 *   autoStart: true,
 *   onComplete: () => alert('Время вышло!')
 * });
 */
export const useCountdown = (options: UseCountdownOptions = {}): UseCountdownResult => {
  const { initialSeconds = 0, autoStart = false, onComplete, interval = 1000 } = options;

  const [seconds, setSeconds] = useState<number>(initialSeconds);
  const [isActive, setIsActive] = useState<boolean>(autoStart);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const savedCallback = useRef<(() => void) | undefined>(onComplete);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Обновляем сохраненный колбэк при изменении onComplete
  useEffect(() => {
    savedCallback.current = onComplete;
  }, [onComplete]);

  // Сбросить таймер
  const reset = useCallback(() => {
    setIsActive(false);
    setSeconds(initialSeconds);
    setIsCompleted(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [initialSeconds]);

  // Запустить таймер
  const start = useCallback(
    (newSeconds?: number) => {
      const startValue = newSeconds !== undefined ? newSeconds : initialSeconds;
      setSeconds(startValue);
      setIsActive(true);
      setIsCompleted(false);
    },
    [initialSeconds],
  );

  // Приостановить таймер
  const pause = useCallback(() => {
    setIsActive(false);
  }, []);

  // Возобновить таймер
  const resume = useCallback(() => {
    if (seconds > 0) {
      setIsActive(true);
    }
  }, [seconds]);

  // Эффект для управления таймером
  useEffect(() => {
    if (!isActive) return;

    timerRef.current = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          // Таймер завершен
          if (timerRef.current) clearInterval(timerRef.current);
          setIsActive(false);
          setIsCompleted(true);

          // Вызываем колбэк, если он существует
          if (savedCallback.current) {
            savedCallback.current();
          }

          return 0;
        }
        return prevSeconds - 1;
      });
    }, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, interval]);

  return {
    seconds,
    isActive,
    start,
    reset,
    pause,
    resume,
    isCompleted,
  };
};
